import {useCallback, useEffect, useRef, useState} from 'react'
import {RefreshCw, ExternalLink} from 'lucide-react'
import {Text} from '@/components/tailwind/text'
import {Badge} from '@/components/tailwind/badge'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/tailwind/table'
import {useAdminTranslation} from "@/i18n/hooks"
import {useAuth} from '@/context/use-auth'
import LoadingSpinner from "@/components/loader-spinner"
import {getAuditLogs} from '@/services/admin-audit-log-services'
import type {AuditLogEntry} from '@/services/admin-audit-log-services'
import {formatDateTimeString} from '@/lib/utils'
import {Subheading} from '@/components/tailwind/heading'
import {Link} from 'react-router-dom'
import log from '@/lib/logger'

const PREVIEW_SIZE = 10

function formatDetails(details: string | null, t: (key: string) => string): string {
    if (!details) return '-'
    try {
        const parsed = JSON.parse(details)
        if (parsed.changedFields) {
            const fieldLabels = (parsed.changedFields as string[]).map(
                (field: string) => t(`adminSettings.auditLog.fieldName.${field}`)
            )
            return fieldLabels.join(', ')
        }
        if (parsed.setting) {
            const settingLabel = t(`adminSettings.auditLog.settingName.${parsed.setting}`)
            const value = typeof parsed.value === 'boolean'
                ? t(`adminSettings.auditLog.booleanValue.${parsed.value ? 'yes' : 'no'}`)
                : parsed.value
            return `${settingLabel}: ${value}`
        }
        if (parsed.name && parsed.role) {
            return `${parsed.name} (${parsed.role})`
        }
        if (parsed.filename) {
            return parsed.filename
        }
        if (parsed.importStatus) {
            return t(`adminSettings.auditLog.importStatus.${parsed.importStatus}`)
        }
        if (typeof parsed.points === 'number') {
            return `${parsed.points} ${t('adminSettings.auditLog.pointsUnit')}`
        }
        return details
    } catch {
        return details
    }
}

function getActionBadgeColor(action: string): "green" | "blue" | "red" | "amber" | "zinc" {
    switch (action) {
        case 'CREATED':
            return 'green'
        case 'UPDATED':
            return 'blue'
        case 'DELETED':
        case 'SOFT_DELETED':
            return 'red'
        case 'PASSWORD_CHANGED':
        case 'PASSWORD_RESET_COMPLETED':
            return 'amber'
        case 'SET_DEFAULT':
            return 'zinc'
        case 'EXPORTED':
            return 'blue'
        case 'DONATED':
            return 'green'
        case 'IMPORT_CONFIRMED':
            return 'green'
        case 'DONATION_REVERSED':
            return 'amber'
        default:
            return 'zinc'
    }
}

export function SectionAuditLog() {
    const {t} = useAdminTranslation()
    const {token} = useAuth()

    const [loading, setLoading] = useState(true)
    const [entries, setEntries] = useState<AuditLogEntry[]>([])
    const [totalElements, setTotalElements] = useState(0)
    const sectionRef = useRef<HTMLDivElement>(null)
    const lastFetchRef = useRef<number>(0)

    const fetchAuditLogs = useCallback(() => {
        if (!token) return
        setLoading(true)
        getAuditLogs(token, {excludeEntityTypes: ['MEMBER'], size: PREVIEW_SIZE})
            .then((result) => {
                setEntries(result.entries)
                setTotalElements(result.totalElements)
            })
            .catch((err) => {
                log.error('Failed to load audit logs:', err)
                setEntries([])
            })
            .finally(() => {
                setLoading(false)
                lastFetchRef.current = Date.now()
            })
    }, [token])

    useEffect(() => {
        fetchAuditLogs()
    }, [fetchAuditLogs])

    useEffect(() => {
        const element = sectionRef.current
        if (!element) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && Date.now() - lastFetchRef.current > 5000) {
                    fetchAuditLogs()
                }
            },
            {threshold: 0.1},
        )
        observer.observe(element)
        return () => observer.disconnect()
    }, [fetchAuditLogs])

    return (
        <div ref={sectionRef}>
            <div className="flex items-center justify-between">
                <div>
                    <Subheading>{t('adminSettings.auditLog.title')}</Subheading>
                    <Text className="mt-1">{t('adminSettings.auditLog.description')}</Text>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchAuditLogs}
                        disabled={loading}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                        title={t('adminSettings.auditLog.refresh')}
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="mt-4">
                    <LoadingSpinner />
                </div>
            ) : entries.length === 0 ? (
                <Text className="mt-4 text-zinc-500 dark:text-zinc-400">
                    {t('adminSettings.auditLog.noEntries')}
                </Text>
            ) : (
                <>
                    <div className="mt-4">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('adminSettings.auditLog.columns.timestamp')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.user')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.entityType')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.action')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.entityName')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.details')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell className="whitespace-nowrap text-sm">
                                            {formatDateTimeString(entry.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.createdByName || '-'}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {t(`adminSettings.auditLog.entityType.${entry.entityType}`)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={getActionBadgeColor(entry.action)}>
                                                {t(`adminSettings.auditLog.action.${entry.action}`)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.entityName || '-'}
                                        </TableCell>
                                        <TableCell className="text-sm max-w-xs truncate" title={formatDetails(entry.details, t)}>
                                            {formatDetails(entry.details, t)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    {totalElements > 0 && (
                        <div className="mt-3 text-center">
                            <Link
                                to="/admin/audit-log"
                                className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                            >
                                {totalElements > PREVIEW_SIZE
                                    ? t('adminSettings.auditLog.showAll', {count: totalElements})
                                    : t('adminSettings.auditLog.fullPage.title')}
                                <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
